import { buildCollection, compileReviews, updatePage } from '@libs/server'
import {
  getLocalState,
  setPrefix,
  syncLocalTab
} from '@libs/client/hooks/useGlobalState/StateLocal'
import { paygate_adapter, store_setting_template, store_type } from '@libs/common/models/constant'
import { ParsedUrlQuery } from 'querystring'
import { customConfig, hardcodePaygateContact } from './hardcode'
import { initRDT } from '@libs/client/hooks/useGlobalState/ReduxDevTool'
import { setGlobalState } from '@libs/client'
import https from 'https'
import axios from 'axios'

const agent = new https.Agent({
  rejectUnauthorized: false // This bypasses SSL certificate validation
})

const TIMEOUT_LOAD_INACTIVE = 1000 * 5 // 5 seconds
const getDefaultStore = (): S3Types.store => {
  return {
    setting: {
      template: store_setting_template.default,
      settings: {
        general: {
          id: '',
          description: '',
          domain: '',
          favicon: '',
          logo: {
            image: ''
          },
          meta: {
            description: '',
            dmca_site_verification: '',
            facebook_domain_verification: '',
            google_site_verification: '',
            image: '',
            title: ''
          },
          name: '',
          publisher_id: '',
          store_fee: {},
          after_sale: {},
          title: '',
          type: store_type.SELLPAGE,
          azp_trusted_domains: ''
        }
      }
    },
    pages: {}
  }
}

const getDefaultServerState = (): ServerState => {
  return {
    domains: {},
    publisher: {} as any,
    store: {},
    promotions: { default: {} as S3Types.promotions },
    paygates: {},
    static: {},
    isPreview: false,
    analytics: {
      pixel_ids: [],
      tiktok_ids: [],
      snapchat_ids: [],
      pinterest_ids: [],
      axon_ids: [],
      google_conversion_id: '',
      google_conversion_label: '',
      content_id: ''
    },
    customConfig: {},
    settingDefault: {
      promotions: {},
      setting: {
        template: store_setting_template.default,
        settings: {
          general: {
            after_sale: {}
          }
        }
      }
    },
    surveys: {
      config: {}
    },
    isAzpTrustedDomain: false
  }
}

export interface ServerState {
  domains?: S3Types.domains
  publisher?: S3Types.publisher
  store?: S3Types.store
  promotions?: {
    default: S3Types.promotions
    [slug: string]: S3Types.promotions
  }
  paygates?: { creditcard?: S3Types.paygate; paypalex?: S3Types.paygate }
  static?: {
    [static_slug: string]: S3Types.default_store_static_page
  }
  isPreview?: boolean
  analytics?: S3Types.analytics
  customConfig?: any
  settingDefault?: S3Types.default_store
  surveys?: S3Types.surveys
  isAzpTrustedDomain?: boolean
}

export interface InactiveResponse {
  page_data: S3Types.page
  page_version: S3Types.page
}

export function initializeClientState() {
  const INIT_STATE = {
    ...setPrefix(window?.__NEXT_DATA__?.props?.pageProps, ''),
    ...getLocalState()
  }
  setGlobalState(INIT_STATE, true)
  initRDT(INIT_STATE)
  // Set to false to avoid clearing state, this should be changed once we apply sync between tabs of browser.
  syncLocalTab((data: any) => setGlobalState(data))
}

export async function initializeServerState(
  domain: string,
  subpath: string,
  pathname: string,
  query?: ParsedUrlQuery,
  versionCookie?: string,
  pVersionCookie?: string,
  cookies?: { [key: string]: string }
) {
  const dataFromBuild = await buildServerState({
    domain,
    subpath,
    pathname,
    query,
    versionCookie,
    pVersionCookie,
    cookies
  })
  setGlobalState(dataFromBuild, true)

  return dataFromBuild
}

async function buildServerState(params: {
  domain: string
  subpath: string
  pathname: string
  query?: ParsedUrlQuery
  versionCookie?: string
  pVersionCookie?: string
  cookies?: { [key: string]: string }
}) {
  const { domain, subpath, versionCookie, pVersionCookie, cookies } = params
  const serverState = getDefaultServerState()

  serverState.settingDefault = process._repository?.default_store_client

  if (process._repository.domains?.[domain]) {
    serverState.domains = { [domain]: process._repository?.domains[domain] }
  }

  const store_id = serverState?.domains?.[domain]?.store_id || ''
  const store = process._repository?.stores?.[store_id]
  const defaultStore = process._repository?.default_store

  let storeData = getDefaultStore()
  if (store_id) {
    const pages = process._repository?.pages || {}

    const publisher =
      process._repository.publishers?.[store?.setting?.settings?.general?.publisher_id ?? '1']

    if (store && store.pages) {
      if (store.setting?.settings?.general?.type === store_type.SELLPAGE) {
        if (store.pages?.setting) {
          const id = store.pages?.setting?.settings?.general?.id

          const pageVersionId =
            pVersionCookie || store.pages?.setting?.settings.general.priority_version_id
          const pageVersion = process._repository.page_versions?.[pageVersionId || '']
          const isPageInactiveInMq =
            !store?.pages?.setting && process._repository._mq_inactive?.pages?.pages?.[id]

          storeData = {
            setting: {
              settings: store.setting.settings,
              template: store.setting.template
            },
            pages: store.pages[pageVersionId || ''] || {}
          }
          serverState.promotions = {
            default: { ...defaultStore?.promotions, ...storeData.pages?.promotions?.default }
          }
          const isPageVersionInactiveInMq =
            !pageVersion &&
            process._repository._mq_inactive?.page_versions?.page_versions?.[pageVersionId || '']

          serverState.analytics = { ...(storeData.pages?.setting?.settings.analytics || {}) } as any

          if (isPageInactiveInMq || isPageVersionInactiveInMq) {
            await handleInactive(
              domain,
              isPageInactiveInMq,
              !!isPageVersionInactiveInMq,
              id,
              pageVersionId || '',
              storeData,
              true,
              subpath
            )
          }
          if (storeData.setting) {
            storeData.setting.settings.version = versionCookie
          }
        }
      } else {
        if (subpath && store?.pages?.[subpath]) {
          const pageId = store?.pages?.[subpath]?.setting?.settings?.general?.page_id || ''
          const page = pages?.[pageId] as S3Types.pages
          const pageVersionId =
            pVersionCookie || page?.setting?.settings?.general?.priority_version_id || ''
          const pageVersion = process._repository.page_versions?.[pageVersionId]

          storeData = {
            setting: {
              settings: store.setting?.settings || ({} as any),
              template: store.setting?.template || ({} as any)
            },
            pages: {
              [subpath]: {
                ...(store.pages[subpath]?.[pageVersionId || ''] || {})
              } // Keep only the setting key
            }
          }

          serverState.promotions = {
            default: defaultStore?.promotions || {},
            ...(storeData.pages?.[subpath]?.[pageVersionId]?.promotions || {})
          }
          if (storeData?.pages) {
            serverState.analytics = {
              ...(storeData?.pages[subpath]?.setting?.settings.analytics || {})
            } as any
          }

          const isPageInactiveInMq =
            !page?.setting && process._repository._mq_inactive?.pages?.pages?.[pageId] //inactive but not in mq
          const isPageVersionInactiveInMq =
            !pageVersion?.setting &&
            process._repository._mq_inactive?.page_versions?.page_versions?.[pageVersionId] //inactive but not in mq
          if (isPageInactiveInMq || isPageVersionInactiveInMq) {
            await handleInactive(
              domain,
              !!isPageInactiveInMq,
              !!isPageVersionInactiveInMq,
              pageId,
              pageVersionId,
              storeData,
              false,
              subpath
            )
          }
        }
        if (!store?.pages?.[subpath]) {
          storeData.setting = store.setting
        }
        // Case homepage of multistore
        if (!subpath) {
          storeData = JSON.parse(JSON.stringify(store)) as S3Types.store
          Object.entries(storeData?.pages || {}).forEach(([page_path, page]) => {
            if (storeData?.pages) {
              const key = `pversion${page_path ? `_${page_path}` : ''}`
              const pageVersionId = `${
                cookies?.[key] ||
                page?.setting?.settings?.general?.priority_version_id ||
                Object.keys(page?.setting?.settings?.traffic_config || {})?.[0] ||
                ''
              }`
              storeData.pages[page_path] = page?.[pageVersionId]
            }
          })

          storeData.collection = buildCollection(storeData, versionCookie || '')
          storeData.pages = {}
          serverState.promotions = {
            default: defaultStore?.promotions
          }
        }
        if (storeData.setting) storeData.setting.settings.version = versionCookie
        if (storeData.setting && process._repository?.pbases) {
          const pageId = storeData?.pages?.[subpath]?.setting?.settings?.general?.page_id || ''
          const page = pages?.[pageId] as S3Types.pages
          const pbaseId = page?.setting?.settings?.general?.pbase_id || ''
          storeData.setting.settings.general.logrocket = validateStatusLogRocket(
            process._repository?.pbases?.[pbaseId],
            domain,
            subpath
          )
          storeData.setting.settings.general.product_type_id =
            process._repository?.pbases?.[pbaseId]?.google_attributes?.product_type_id || ''
        }
      }
    }

    serverState.store = storeData

    if (publisher) {
      serverState.publisher = publisher
      const storeAddress = store?.setting?.settings?.configurations?.store_address || ''
      serverState.paygates = paymentGatewaysHardcoded(
        domain,
        getPaymentGateways(publisher),
        storeAddress,
        versionCookie
      )
    }

    const trustedDomains =
      process?._repository?.default_store?.setting?.settings?.general?.azp_trusted_domains
    if (store?.setting) {
      store.setting.settings.isAzpTrustedDomain = !!(
        trustedDomains && trustedDomains?.includes(domain)
      )
    }

    serverState.customConfig = customConfig(domain, subpath)
  }

  serverState.isPreview = false
  serverState.surveys = process._repository?.surveys

  return serverState
}

const getPaymentGateways = (publisher: S3Types.publisher) => {
  const payments = publisher.payments
  const creditcardType = payments?.creditcard
  const paypalexType = payments?.paypalex
  const creditcardId =
    creditcardType === 'DEFAULT'
      ? process._repository?.default_paygate?.creditcard
      : creditcardType === 'SPECIFIC'
      ? payments?.creditcard_id
      : null
  const paypalexId =
    paypalexType === 'DEFAULT'
      ? process._repository?.default_paygate?.paypalex
      : paypalexType === 'SPECIFIC'
      ? payments?.paypalex_id
      : null
  return {
    ...(creditcardId && { creditcard: process._repository.paygates?.[creditcardId] }),
    ...(paypalexId && { paypalex: process._repository.paygates?.[paypalexId] })
  }
}

const validateStatusLogRocket = (pbases: S3Types.pbase, domain: string, slug = '') => {
  const expiration =
    pbases?.logrocket_config?.[`${domain}${slug?.length ? `/${slug}` : ''}`]?.expiration || 0
  return expiration > new Date()?.getTime()
}

const paymentGatewaysHardcoded = (
  domain: string,
  paygates: ServerState['paygates'],
  storeAddress = '',
  version = ''
) => {
  const paygateContact = hardcodePaygateContact(domain)
  return {
    ...(paygates?.creditcard && {
      creditcard: {
        ...paygates?.creditcard,
        contact: {
          ...paygates?.creditcard.contact,
          ...paygateContact,
          ...(storeAddress ? { address: storeAddress } : {})
        },
        ...(version == 'br1'
          ? {
              id: process.env.BR_ID || '6np02be0mn48qm8mdsb3rg2xx', // paygateId
              adapter: paygate_adapter.BRAINTREE
            }
          : {})
      }
    }),
    ...(paygates?.paypalex && {
      paypalex: {
        ...paygates?.paypalex,
        contact: {
          ...paygates?.paypalex.contact,
          ...paygateContact,
          ...(storeAddress ? { address: storeAddress } : {})
        }
      }
    })
  }
}

export async function initializePreviewServerState(pathname: string, query?: ParsedUrlQuery) {
  const INIT_STATE = await buildPreviewServerState({ pathname, query })
  setGlobalState(INIT_STATE, true)

  return INIT_STATE
}

async function buildPreviewServerState(params: { pathname: string; query?: ParsedUrlQuery }) {
  const { pathname, query } = params
  const serverState: ServerState = {}

  serverState.store = getDefaultStore()

  const _static: {
    [static_slug: string]: S3Types.default_store_static_page
  } = {}
  if (pathname.startsWith('/static') && query?.static_slug) {
    if (process._repository?.default_store?.setting?.static?.[query.static_slug as string]) {
      _static[query.static_slug as string] =
        process._repository?.default_store?.setting?.static?.[query.static_slug as string]
    }

    serverState.static = _static
  }

  serverState.isPreview = true
  return serverState
}

const handleInactive = async (
  domain: string,
  isPageInactiveInMq: boolean,
  isPageVersionInactiveInMq: boolean,
  pageId: string,
  pageVersionId: string,
  storeData: S3Types.store,
  isSingleStore: boolean,
  subpath = ''
) => {
  if (!isPageInactiveInMq && !isPageVersionInactiveInMq) {
    return
  }
  const requestPageId =
    isPageVersionInactiveInMq && pageVersionId ? (isPageInactiveInMq ? pageId : '0') : pageId
  const requestPageVersionId =
    pageVersionId || (isPageInactiveInMq && !pageVersionId ? '0' : pageVersionId)
  const { page_data, page_version } = await getS3InactiveApi(
    domain,
    requestPageId,
    requestPageVersionId
  )
  if (!page_data || !page_version) {
    return
  }
  updatePage(page_version)
  const updatedPage = {
    ...page_data,
    ...page_version,
    setting: {
      ...(page_data?.setting || {}),
      ...(page_version?.setting || {}),
      settings: {
        ...(page_version?.setting?.settings || {}),
        ...(page_data?.setting?.settings || {}),
        general: {
          ...(page_data?.setting?.settings?.general || {}),
          ...(page_version?.setting?.settings?.general || {})
        } as S3Types.page_settings_general
      } as S3Types.page_settings
    } as S3Types.page_setting
  } as S3Types.page
  const { reviews, ratings } = compileReviews(
    updatedPage.reviews || [],
    updatedPage.ratings as S3Types.page_rating
  )
  if (!storeData.pages) storeData.pages = {}
  updatedPage.pages.reviews = reviews as S3Types.page_reviews
  updatedPage.pages.ratings = ratings

  if (isSingleStore) {
    storeData.pages = updatedPage
  } else {
    storeData.pages[subpath] = updatedPage
  }
}

const getS3InactiveApi = async (baseHost = '', pageId: string, pageVersionId: string) => {
  try {
    const route = `${
      process.env.SERVICE_NAME || 'http://cms-template-default-internal:80'
    }/iapi/page-inactive/${pageId || ''}/${pageVersionId || ''}`
    console.log(`Load inactive in domain ${baseHost} with route ${route}`)
    const response = await axios.get(route, {
      httpsAgent: agent,
      timeout: TIMEOUT_LOAD_INACTIVE
    })
    return response?.data as InactiveResponse
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      console.error('Timeout error while loading inactive page data.')
      return {
        page_data: null,
        page_version: null
      }
    }
    throw new Error(`Error with load inactive page id ${pageId}, page version id ${pageVersionId}`)
  }
}
